import React, { useState } from "react";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import apiClient from "../../axiosClient";
// Custom types and defs
import huntGroupColDef from "./colDef/huntGroupConfig";
import HuntGroup from "./interface/huntGroup";
import AddHuntGroupModal, { NewHuntGroupRequest } from "./modal/addHuntGroup";
import { apiCallWrapper } from "../../utils/commonUtils";
import ActionConfirmationModal from "../modals/actionConfirmationModal";
import ManageHuntGroupAgentsModal from "./modal/manageHuntGroupAgents";
import Agent from "./interface/agent";

interface HuntGroupConfigComponentProp {
	loading: boolean,
	huntGroups: HuntGroup[],
	refreshHuntGroups: () => void,
	refreshAgents: () => void,
	availableAgents: Agent[]
}

const HuntGroupConfig: React.FC<HuntGroupConfigComponentProp> = ({ loading, huntGroups, availableAgents, refreshHuntGroups, refreshAgents }) => {
	const [selectedRows, setSelectedRows] = useState<HuntGroup[]>([]);
	const [openAddHuntGroupModal, setAddHuntGroupModalVisibility] = useState<boolean>(false);
	const [openDeactivateAction, setDeactivateActionVisibility] = useState<boolean>(false);
	const [openAddLicenseModal, setAddLicenseModalVisibility] = useState<boolean>(false)

	const addNewHuntGroup = async (newHuntGroupRequest: NewHuntGroupRequest) => {

		if (huntGroups.findIndex((group) => group.hunt_group_name === newHuntGroupRequest.huntGroupName) != -1) {
			alert("Hunt Group with this name already exist.");
		}

		const payload = {
			hunt_group_name: newHuntGroupRequest.huntGroupName,
			agency_name: newHuntGroupRequest.selectedAgency
		};

		const response = await apiCallWrapper(() => apiClient.post(`/api/v1/tcn_config/hunt_group`, payload));
		if (response && (response.status === 200 || response.status === 201)) {
			alert("Hunt Group added successfully");
			refreshHuntGroups();
			setAddHuntGroupModalVisibility(false);
		}
	};

	const handleHuntGroupDeactivation = async () => {
		if (selectedRows.length > 0) {
			let hunt_group_id = selectedRows[0].hunt_group_id;

			const response = await apiCallWrapper(() => apiClient.delete(`/api/v1/tcn_config/hunt_group/${hunt_group_id}`));
			if (response.status === 200 || response.status === 201) {
				alert("Hunt Group deactivated successfully");
				refreshHuntGroups();
				refreshAgents();
			}
		} else {
			alert("Select a Hunt Group to deactivate");
		}
	}

	const handleSelectRow = (ids: GridRowSelectionModel) => {
		setSelectedRows(
			huntGroups.filter((group: HuntGroup) => Array.from(ids.ids).includes(group.hunt_group_id))
		);
	};

	return (
		<Box sx={{ margin: '10px', display: 'flex', flexDirection: 'column' }}>

			<Stack
				direction="row"
				spacing={2}
				justifyContent="flex-end"
				sx={{ mb: 2 }}
			>
				{selectedRows.length === 1 && (
					<Button variant="contained" onClick={() => setAddLicenseModalVisibility(true)}>Add Agent Licences</Button>
				)}
				{selectedRows.length === 1 && (
					<Button variant="contained" onClick={() => setDeactivateActionVisibility(true)}>Deactivate Hunt Group</Button>
				)}
				<Button variant="contained" onClick={() => setAddHuntGroupModalVisibility(true)}>Add Hunt Group</Button>
			</Stack>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
					<CircularProgress />
				</Box>
			) : (
				<SearchResultGrid
					columns={huntGroupColDef}
					results={huntGroups}
					rowCount={huntGroups.length}
					onSelectRow={handleSelectRow}
					getRowId={(row: HuntGroup) => row.hunt_group_id}
					disableFilter={false}
					disableSorting={false}
				/>
			)}

			{openAddHuntGroupModal && <AddHuntGroupModal open={openAddHuntGroupModal} onClose={() => setAddHuntGroupModalVisibility(false)} onSubmit={addNewHuntGroup} />}

			{openDeactivateAction && <ActionConfirmationModal
				visible={openDeactivateAction}
				onClose={() => setDeactivateActionVisibility(false)}
				onConfirm={handleHuntGroupDeactivation}
				headerText={"Warning!"}
				bodyText={"This would deactivate hunt group and associated agent licenses."}
			/>}

			{openAddLicenseModal && <ManageHuntGroupAgentsModal 
				open={openAddLicenseModal} 
				onClose={() => setAddLicenseModalVisibility(false)} 
				huntGroup={selectedRows[0]} 
				availableAgents={availableAgents}
				refreshAgents={() => refreshAgents()}
			/>}

		</Box>
	);
};

export default HuntGroupConfig;